// src/components/PortfolioContent.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useMemo } from "react";
import { projects, categoryLabels, categoryDescriptions } from "@/data/portfolio";
import ProjectCard from "@/components/portfolio/ProjectCard";

type CategoryFilter = "all" | "ppf" | "ceramic" | "tinting";

const filters: Array<{ value: CategoryFilter; label: string }> = [
  { value: "all", label: "Kõik" },
  { value: "ppf", label: categoryLabels.ppf },
  { value: "ceramic", label: categoryLabels.ceramic },
  { value: "tinting", label: categoryLabels.tinting },
];

export default function PortfolioContent() {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>("all");

  const visibleProjects = useMemo(() => {
    if (activeFilter === "all") return projects;
    return projects.filter((project) => project.category === activeFilter);
  }, [activeFilter]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "PPF Garage Portfolio – Meie tööd",
    description:
      "PPF Garage valitud portfoolio töödest – PPF kiletamine, keraamiline kaitse ja klaaside toonimine Tallinnas ja Harjumaal",
    url: "https://ppf.ee/tood",
    itemListElement: visibleProjects.map((project, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "CreativeWork",
        "@id": `https://ppf.ee/tood#${project.slug}`,
        name: project.title,
        description: project.shortDescription,
        image: [project.images.before, project.images.after],
        keywords: project.keywords?.join(", ") || project.tags.join(", "),
        offers: {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: categoryLabels[project.category],
            description: categoryDescriptions[project.category],
          },
        },
        provider: {
          "@type": "LocalBusiness",
          name: "PPF Garage OÜ",
          url: "https://ppf.ee",
        },
      },
    })),
  };

  return (
    <section className="min-h-screen bg-primary text-text-main">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="relative flex min-h-[52vh] flex-col justify-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-primary/95 to-primary/40" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link href="/" className="text-text-muted transition-colors hover:text-accent">
              Avaleht
            </Link>
            <span className="text-text-muted">/</span>
            <span className="text-accent">Tööd</span>
          </nav>

          <div className="max-w-3xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-accent sm:mb-4 sm:text-sm">
              Portfolio
            </p>
            <h1 className="font-heading text-[40px] font-extrabold leading-[1.1] text-text-main md:text-[56px]">
              Meie tööd ja valmisprojektid
            </h1>
            <p className="mt-4 max-w-3xl font-body text-[16px] leading-relaxed text-text-muted md:text-[18px]">
              Tutvuge valitud näidete ja tööprotsessiga, mis näitavad meie
              premium-autokaitse standardeid ning mille alusel koostame igale
              kliendile sobiva lahenduse. PPF kiletamine, keraamiline kaitse ja
              klaaside toonimine Tallinnas ja Harjumaal.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-secondary px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 flex flex-wrap gap-2 sm:gap-3">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                aria-pressed={activeFilter === filter.value}
                className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-300 sm:px-6 sm:py-3 ${
                  activeFilter === filter.value
                    ? "bg-accent text-primary"
                    : "border border-white/10 bg-primary/70 text-text-main hover:border-accent/40 hover:bg-accent hover:text-primary"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="mb-10 text-sm text-text-muted">
            Näidatud {visibleProjects.length} / {projects.length} projekti
          </div>

          <div className="mb-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {visibleProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                detailHref={`/tood/${project.slug}`}
              />
            ))}
          </div>

          {visibleProjects.length === 0 && (
            <div className="rounded-sm border border-white/5 bg-primary/70 py-16 text-center">
              <p className="text-text-muted">
                Valitud kategoorias pole hetkel töid näidatud. Uurige teisi
                kategooriaid.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-primary px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto flex max-w-6xl flex-col items-center">
          <div className="mb-10 inline-flex px-5 py-3">
            <h2 className="text-center font-heading text-3xl font-bold text-text-main sm:text-4xl">
              Meie teenused
            </h2>
          </div>
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              href="/teenused/kaitsekiletamine-ppf"
              aria-label="Loe lähemalt PPF kiletamise kohta"
              className="group flex h-full w-full"
            >
              <div className="flex h-full w-full flex-col items-start justify-between rounded-sm border border-white/5 bg-secondary/80 p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-secondary/90 sm:p-8">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-text-main transition-colors group-hover:text-accent">
                    PPF kiletamine
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-muted">
                    Nähtamatu ja tugev kaitse kivitäkete eest.
                  </p>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-transform group-hover:translate-x-1">
                  Vaata teenust
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </Link>
            <Link
              href="/teenused/keraamiline-kaitse"
              aria-label="Loe lähemalt keraamilise kaitse kohta"
              className="group flex h-full w-full"
            >
              <div className="flex h-full w-full flex-col items-start justify-between rounded-sm border border-white/5 bg-secondary/80 p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-secondary/90 sm:p-8">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-text-main transition-colors group-hover:text-accent">
                    Keraamiline kaitse
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-muted">
                    Sügav läige ja pikaajaline kaitse ilmastiku eest.
                  </p>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-transform group-hover:translate-x-1">
                  Vaata teenust
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </Link>
            <Link
              href="/teenused/klaaside-toonimine"
              aria-label="Loe lähemalt klaaside toonimise kohta"
              className="group flex h-full w-full"
            >
              <div className="flex h-full w-full flex-col items-start justify-between rounded-sm border border-white/5 bg-secondary/80 p-6 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-secondary/90 sm:p-8">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-text-main transition-colors group-hover:text-accent">
                    Klaaside toonimine
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-text-muted">
                    Privaatsus ja kaitse kahjuliku UV-kiirguse eest.
                  </p>
                </div>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-transform group-hover:translate-x-1">
                  Vaata teenust
                  <span aria-hidden="true">→</span>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </section>
  );
}
